import type { AlignStackPresetProp, AlignStackProps } from '../../utilities/AlignStack/AlignStack';
import { colors, darkThemeSelector, styled } from '../../stitches.config';
import { BodySansSizes } from '../../text/Body';
import { CaptionSansSizes } from '../../text/Caption';
import { SmallSansSizes } from '../../text/Small';
import { Text } from '../../text/Text';
import { AlignStack } from '../../utilities/AlignStack/AlignStack';

export type CounterProps = {
  /**
   * The count to display.
   */
  count: number;
  /**
   * The preset to use for the counter.
   */
  preset?: AlignStackProps['preset'];
  /**
   * The total to display.
   */
  total: number;
};

const CounterCount = styled(Text);
CounterCount.displayName = 'CounterCount';

const CounterChar = styled(Text, {
  padding: '0 $2',
});
CounterChar.displayName = 'CounterChar';

const CounterTotal = styled(Text, {
  whiteSpace: 'nowrap',
  color: colors.bodyNeutralLight,

  [darkThemeSelector]: {
    color: colors.bodyNeutralDark,
  },

  variants: {
    preset: {
      caption: {
        ...CaptionSansSizes,
      },
      small: {
        ...SmallSansSizes,
      },
      body: {
        ...BodySansSizes,
      },
    },
  },
});
CounterTotal.displayName = 'CounterTotal';

const CounterContainer = styled(AlignStack);
CounterContainer.displayName = 'CounterContainer';

type CounterTotalPreset = 'caption' | 'small' | 'body';

function resolveTotalPreset(preset: AlignStackPresetProp): CounterTotalPreset {
  switch (preset) {
    case 'caption':
      return 'caption';
    case 'small':
    case 'body':
    case 'subheading':
      return 'small';
    default:
      return 'body';
  }
}

export function Counter({ count, preset = 'body', total }: CounterProps) {
  const totalPreset = resolveTotalPreset(preset);
  const hasCount = count !== null && count !== undefined;
  const hasTotal = total !== null && total !== undefined;

  if (!hasCount && !hasTotal) {
    return null;
  }

  return (
    <CounterContainer align="baseline" gap={0} preset={preset} width="fit-content">
      {hasCount && (
        <CounterCount weight="bold" family="monospace">
          {count}
        </CounterCount>
      )}
      {hasTotal ? (
        <CounterTotal preset={totalPreset} weight="bold" family="monospace">
          <CounterChar>/</CounterChar>
          {total}
        </CounterTotal>
      ) : undefined}
    </CounterContainer>
  );
}
